<template>
    <div class="d-flex flex-column" :style="rootContainerStyle">
        <div v-if="!isPreview || (isPreview && questionSections.length > 1)" ref="top-bar-container" class="top-bar-container px-4 py-2 order-0">
            <top-bar-customer-section
                v-if="!isPreview"
                :context="context"
                :provider="provider"
                scrollElementClass="booking-container" />
            <form-section-controller
                :style="{position: 'relative', top: '4px'}"
                ref="form-sections-controller"
                :form-padding-top="sectionPaddingTop"
                :sections="questionSections" />
        </div>

        <section class="mb-3 col-12 col-md-9 col-lg-7 ml-auto mr-auto order-0">
            <div v-if="isPreview" class="brief text-center">
                {{ $t('this_is_preview') }}
            </div>
            <div v-else-if="isServiceInPreview" class="brief text-center">
                <b>{{$t(
                    service.auto_visible_at
                    ? 'message.is_preview_cant_book_with_schedule' : 'message.is_preview_cant_book', {
                        start: autoVisibleFormatted(service)
                    })}}</b>
            </div>
            <i18n v-else class='brief' path="booking.brief">
                <span class="brief-highlight">{{date}} {{formattedStartTime}} - {{date === endDate ? '' : endDate}} {{formattedEndTime}}</span>
                <span class="brief-highlight">{{memberUser.name}}</span>
                <span class="brief-highlight">{{service.title}}</span>
            </i18n>
        </section>

        <section v-if="questions.length > 0"
            class="card mb-3 col-12 col-md-9 col-lg-7 ml-auto mr-auto px-0 question-form-section order-1">
            <header class="card-header text-center">
                {{$t('message.questions_for_booking')}}
            </header>
            <div class="card-block w-100 p-0">
                <template v-for="q in validQuestions">
                    <question-section
                        v-if="isQuestionSection(q)"
                        ref="question-section-ref"
                        class="question-section"
                        :style="{scrollMarginTop: sectionPaddingTop}"
                        :key="q.id"
                        :question="q"
                        :providerId="context.providerId"
                        :context="context"
                        :index="getQuestionSectionIndex(q)"
                    />
                    <div v-else class="form-group row question-container" :style="styleForQuestion(q)" style="padding: 16px;">
                        <question-multiple-choice-with-note
                            v-if="q.question.answer_type === Constants.Question.AnswerType.MultipleChoiceWithNote"
                            ref="question-ref" :question="q" :providerId="context.providerId" :context="context">
                        </question-multiple-choice-with-note>

                        <question-info v-if="(q.question.answer_type === Constants.Question.AnswerType.Info)"
                            ref="question-ref" :question="q" :providerId="context.providerId" :context="context">
                        </question-info>

                        <question-choice
                            v-if="(q.question.answer_type === Constants.Question.AnswerType.SingleChoice || q.question.answer_type === Constants.Question.AnswerType.MultipleChoice)"
                            ref="question-ref" :question="q" :providerId="context.providerId" :context="context">
                        </question-choice>

                        <question-text
                            v-if="q.question.answer_type === Constants.Question.AnswerType.Paragraph || q.question.answer_type === Constants.Question.AnswerType.ShortAnswer"
                            ref="question-ref" :question="q" :providerId="context.providerId" :context="context">
                        </question-text>

                        <question-shopping v-if="q.question.answer_type === Constants.Question.AnswerType.Shopping"
                            ref="question-ref" :provider="provider" :question="q" :providerId="context.providerId"
                            :context="context" />

                        <question-date v-if="q.question.answer_type === Constants.Question.AnswerType.Date"
                            ref="question-ref" :question="q" :provider="provider" :context="context"></question-date>

                        <question-file-upload v-if="q.question.answer_type === Constants.Question.AnswerType.FileUpload"
                            ref="question-ref" :question="q" :providerId="context.providerId" :context="context">
                        </question-file-upload>

                        <question-linear-scale v-if="q.question.answer_type === Constants.Question.AnswerType.LinearScale"
                            ref="question-ref" :question="q" :provider="provider" :context="context">
                        </question-linear-scale>

                        <question-address
                            v-if="(q.question.answer_type === Constants.Question.AnswerType.Address)"
                            ref="question-ref"
                            :question="q"
                            :providerId="context.providerId"
                            :context="context"
                        ></question-address>

                        <question-youtube
                            v-if="(q.question.answer_type === Constants.Question.AnswerType.Youtube)"
                            ref="question-ref"
                            :question="q"
                            :providerId="context.providerId"
                            :context="context"
                        ></question-youtube>

                        <question-matrix
                            v-if="(q.question.answer_type === Constants.Question.AnswerType.SingleMatrix || q.question.answer_type === Constants.Question.AnswerType.MultipleMatrix)"
                            ref="question-ref" :question="q" :providerId="context.providerId" :context="context">
                        </question-matrix>
                    </div>
                </template>
            </div>
        </section>

        <template v-if="!isPreview">
            <section
                v-if="!isServiceInPreview"
                ref="customer-information-form-section"
                class="card mb-3 col-12 col-md-9 col-lg-7 ml-auto mr-auto px-0 question-form-section"
                :class="{ 'order-1': !hasLogInEnabled, 'order-0': hasLogInEnabled }"
            >
                <header class="card-header text-center">
                    {{$t('message.your_basic_information')}}
                </header>
                <div class="card-block">
                    <customer-information-form
                        @request-login="onRequestLogin"
                        :logInEnabled="hasLogInEnabled"
                        ref="customer-information-form"
                        :email-required="provider.customer_notification_setting.email_channel_enabled"
                        :provider="provider"
                        :context="context"
                    />
                </div>
            </section>

            <section class="card mb-3 col-12 col-md-9 col-lg-7 ml-auto mr-auto px-0 question-form-section order-1"
                v-if="items.length && !isCartEmpty && isCartLogisticNeeded" ref="logistic-section">
                <header class="card-header text-center">
                    {{$t('message.cart_logistic')}}
                </header>
                <div class="card-block">
                    <cart-logistic :context="context" :provider="provider" ref="logistic-ref" />
                </div>
            </section>

            <section id="cart-section" class="card mb-3 col-12 col-md-9 col-lg-7 ml-auto mr-auto px-0 question-form-section order-1"
                v-if="items.length && !isCartEmpty" ref="cart-section">
                <header class="card-header text-center">
                    {{$t('message.cart')}}
                </header>
                <div class="card-block">
                    <cart :context="context" :provider="provider" ref="cart-ref" @request-login="onRequestLogin" />
                </div>
            </section>

            <section class="card mb-3 col-12 col-md-9 col-lg-7 ml-auto mr-auto px-0 question-form-section order-1"
                v-if="!isCartEmpty && service" ref="payment-method-section">
                <header class="card-header text-center">
                    {{$t('message.cart_payment_method')}}
                </header>
                <div class="card-block">
                    <cart-payment-method :provider="provider" :context="context" :service="service" ref="payment-method-ref" />
                </div>
            </section>
        </template>

        <div v-if="!isPreview" class="col-12 col-md-9 col-lg-7 ml-auto mr-auto text-center mt-2 mb-3 order-1">
            <button v-if="!isServiceInPreview" :disabled="isPreview" class="btn btn-primary btn-block theme-tinted-background theme-tinted-border btn-check mt-2 font-weight-bold"
                @click="attemptSubmit">{{$t('message.submit')}}</button>
            <div class="text-muted text-center" v-else>
                <b>{{$t(
                    service.auto_visible_at
                    ? 'message.is_preview_cant_book_with_schedule' : 'message.is_preview_cant_book', {
                        start: autoVisibleFormatted(service)
                    })}}</b>
            </div>
        </div>

        <sms-verification-popup v-if="showSmsVerification" :country-item="countryItemTmpField"
            :phone-number="phoneNumberTmpField" @sms-code-input="submitFormWithCode"
            @close="showSmsVerification = false"></sms-verification-popup>
    </div>
</template>

<script>
import Constants from '../../constants';
import api from '../../mixins/api';
import promptConfirm from '../../mixins/prompt-confirm';
import vuePhoneInput from '../../components/vue-phone-input/index.vue';
import '../../../sass/vue-phone-input.scss';
import RootStore from '../../stores/root';
import { mapActions, mapGetters } from 'vuex';
import CartLogistic from '../CartLogistic';
import CartPaymentMethod from '../CartPaymentMethod';
import LoginBox from '../LoginBox.vue';
import TopBarCustomerSection from '../common/TopBarCustomerSection.vue';
import moment from 'moment';
import { EventBook, EventPurchase, EventVerifySms } from '../../bu/models/GA4';
import FormSectionController from '../common/FormSectionController.vue';
import CapabilityManager from '../../bu/CapabilityManager';

export default {
    components: { vuePhoneInput, CartLogistic, CartPaymentMethod, LoginBox, TopBarCustomerSection, FormSectionController },
    mixins: [api, promptConfirm],
    store: RootStore,
    props: {
        isPreview: {type: Boolean, default: false},
        provider: {
            type: Object,
            required: true
        },
        questions: {
            type: Array,
            required: true
        },
        preventEmptyBag: {
            type: Boolean,
            default: false
        },
        context: {
            type: Object,
            required: true
        },
        service: {
            type: Object,
            required: true
        },
        requiresSmsVerification: {
            type: Boolean,
            default: false
        },
        memberUser: {
            type: Object,
            required: true
        },
        date: {
            type: String,
            required: true
        },
        endDate: {
            type: String,
            required: false
        },
        startTime: {
            type: String
        },
        endTime: {
            type: String
        }
    },

    data() {
        this.$_capabilityManager = new CapabilityManager(this.provider.grade, this.provider.capabilities);

        for (var i in this.questions) {
            var q = this.questions[i];
            q.content = JSON.parse(q.content);
        }
        this.setPreventEmpty(this.preventEmptyBag);
        return {
            showSmsVerification: false,
            phoneCallingCodeTmpField: null,
            phoneNumberTmpField: null,
            countryItemTmpField: null,
            Constants,
            sectionPaddingTop: '44px'
        };
    },

    async mounted() {
        this.loadDataFromLocalStorage();
        this.getCurrencies();
        this.setCartStart(moment(this.date.replaceAll('/', '-') + ' ' + this.startTime).format('YYYY-MM-DD HH:mm:00'));
        if (this.endDate) {
            this.setCartEnd(moment(this.endDate.replaceAll('/', '-') + ' ' + this.endTime).format('YYYY-MM-DD HH:mm:00'));
        } else if (!this.service.allow_custom_duration && this.service.duration_minutes) {
            const end = moment(this.date.replaceAll('/', '-') + ' ' + this.startTime).add(this.service.duration_minutes, 'minutes');
            this.setCartEnd(end.format('YYYY-MM-DD HH:mm:00'));
        }

        if (this.service && (this.autoRequestServicePayment || this.autoRequestDownPayment)) {
            this.setServiceItem({service: this.service});
        } else {
            this.removeServiceItem();
        }

        try {
            await this.getLoggedInCustomer();
        } catch (e) {
            console.log('no logged in customer');
        }

        if (this.$refs['top-bar-container']) {
            let resizeTimeout;
            const resizeObserver = new ResizeObserver(entries => {
                clearTimeout(resizeTimeout);
                resizeTimeout = setTimeout(() => {
                    const entry = entries[0];
                    const cr = entry.contentRect;
                    this.sectionPaddingTop = `${cr.height + cr.top}px`;
                }, 100);
            });
            resizeObserver.observe(this.$refs['top-bar-container']);
        }
        this.updateQuestionSections();
        this.handlePendingOrder();
    },
    computed: {
        ...mapGetters({
            customer: 'app/customer',
            isCartEmpty: 'cart/isCartEmpty',
            isCartLogisticNeeded: 'cart/isCartLogisticNeeded',
            cartItems: 'cart/items',
            cartPointsDeduction: 'cart/pointsDeduction',
            customer: 'app/customer',
            cartLogistic: 'cart/logistic',
            cartLogisticRule: 'cart/logisticRule',
            cartAddress: 'cart/address',
            cartPaymentMethodId: 'cart/paymentMethodId',
            receiverName: 'cart/receiverName',
            receiverPhone: 'cart/receiverPhone',
            questionSections: 'question-sections/validSections',
            items: 'items/items',
        }),
        rootContainerStyle() {
            return {
                marginTop: `${this.sectionPaddingTop}`
            }
        },
        isServiceInPreview() {
            return this.service.is_preview && this.service.is_disabled;
        },
        selectedPaymentMethod() {
            if (!this.cartPaymentMethodId) {
                return null;
            }
            return this.service.payment_methods.find((m) => m.id == this.cartPaymentMethodId);
        },
        hasLogInEnabled() {
            return !!this.provider.login_setting?.enabled;
        },
        nonEmptyCartItems() {
            return this.cartItems.filter((item) => item.count > 0)
        },
        validQuestions() {
            return _.filter(this.questions, (q) => {
                return this.isQuestionValid(q);
            })
        },
        formattedStartTime() {
            if (!this.startTime) {
                return '';
            }
            var components = this.startTime.split(':');
            components.splice(2, 1);
            return components.join(':');
        },
        formattedEndTime() {
            if (!this.endTime) {
                return '';
            }
            var components = this.endTime.split(':');
            components.splice(2, 1);
            return components.join(':');
        },
        autoRequestServicePayment() {
            if (this.service && this.service.auto_request_payment && this.service.price_policy == 'fixed' && this.service.price_min >= 0) {
                return true;
            } else {
                return false;
            }
        },
        autoRequestDownPayment() {
            const hasShoppingQuestion = this.questions.filter(q => q.question.answer_type === 'ANSWER_TYPE_SHOPPING').length > 0;
            let result = !hasShoppingQuestion && !!this.service.auto_request_down_payment && this.service.down_payment_amount > 0;
            this.$set(this.context, 'autoRequestDownPayment', result);
            return result;
        },
        hasLineModuleEnabled() {
            return this.$_capabilityManager?.isLineModuleAvailable();
        },
        hasLineLoginModuleEnabled() {
            return this.hasLineModuleEnabled
                && !!this.provider.line_integration?.line_login_channel_id
                && !!this.provider.line_integration?.line_login_channel_secret;
        },
        hasLoginBlockerEnabled() {
            return this.hasLineLoginModuleEnabled && this.hasLogInEnabled && !this.customer;
        },
    },

    watch: {
        questions() {
            this.updateQuestionSections();
        },
        cartItems() {
            this.$nextTick(() => {
                this.updateQuestionSections();
            });
        }
    },

    methods: {
        ...mapActions({
            setCartStart: 'cart/setStart',
            setCartEnd: 'cart/setEnd',
            setPreventEmpty: 'cart/setPreventEmpty',
            setServiceItem: 'cart/setServiceItem',
            removeServiceItem: 'cart/removeServiceItem',
            setQuestionSections: 'question-sections/setSections',
            openLoginBox: 'app/openLoginBox',
            closeLoginBox: 'app/closeLoginBox',
            getCurrencies: 'currencies/getCurrencies',
            loadDataFromLocalStorage: 'cart/loadDataFromLocalStorage',
            setLoginWithBookingCookie: 'app/setLoginWithBookingCookie',
        }),
        updateQuestionSections() {
            const defaultRefs = [
                {
                    label: this.$t('message.your_basic_information'),
                    ref: this.$refs['customer-information-form-section'],
                },
                {
                    label: this.$t('message.cart_logistic'),
                    ref: this.$refs['logistic-section'],
                },
                {
                    label: this.$t('message.cart'),
                    ref: this.$refs['cart-section'],
                },
                {
                    label: this.$t('message.cart_payment_method'),
                    ref: this.$refs['payment-method-section'],
                },
            ];
            const result = [];
            this.questions
                .filter(question => question.question.answer_type === Constants.Question.AnswerType.Section)
                .forEach((question, index) => {
                    const questionRefs = this.$refs['question-section-ref'];
                    if (questionRefs) {
                        const questionRef = questionRefs[index];
                        result.push({
                            label: question.content?.meta?.title || this.$t('form.section', {count: index + 1}),
                            ref: questionRef
                        });
                    }
                });

            let final = this.isPreview ? result : result.concat(defaultRefs);
            if (this.hasLogInEnabled && !this.isPreview) {
                const [basicInformationRef, ...otherDefaultRefs] = defaultRefs;
                final = [basicInformationRef, ...result, ...otherDefaultRefs];
            }
            this.setQuestionSections(final)
        },
        styleForQuestion(q) {
            if(!this.hasNextQuestionNotSection(q)) {
                return {
                    marginBottom: 0,
                    borderBottom: 'none'
                };
            }
        },
        isQuestionSection(q) {
            return q.question.answer_type === this.Constants.Question.AnswerType.Section
        },
        hasNextQuestionNotSection(q) {
            const index = this.validQuestions.indexOf(q);
            const result = this.validQuestions[index + 1] && !this.isQuestionSection(this.validQuestions[index + 1]);
            return result;
        },
        onRequestLogin() {
            this.openLoginBox(this.$t('message.please_login_to_proceed_booking'));
        },
        autoVisibleFormatted(service) {
            return moment.tz(service.auto_visible_at, 'GMT').tz(this.provider.timezone).format('YYYY-MM-DD A h:mm');
        },
        isQuestionValid(q) {
            const isChoice = [
                Constants.Question.AnswerType.SingleChoice,
                Constants.Question.AnswerType.MultipleChoice,
                Constants.Question.AnswerType.MultipleChoiceWithNote
            ].indexOf(q.question.answer_type) > -1;

            if (isChoice && !this.hasValidOptions(q)) {
                return false;
            }

            const isMatrix = [
                Constants.Question.AnswerType.SingleMatrix,
                Constants.Question.AnswerType.MultipleMatrix,
            ].indexOf(q.question.answer_type) > -1;

            if (isMatrix && !this.hasValidMatrixOptions(q)) {
                return false;
            }

            return true;
        },
        hasValidOptions(q) {
            if (!q.options || q.options.length === 0) {
                return false;
            }
            return q.options.filter((o) => {
                return o.content_unit.text && o.content_unit.text.length > 0
            }).length > 0;
        },
        hasValidMatrixOptions(q) {
            return q.content.data.rows?.length && q.content.data.columns?.length;
        },
        attemptSubmit() {
            if (this.preventEmptyBag && this.isCartEmpty) {
                this.promptAlert(this.$t('please_select_at_least_1_item'));
                return;
            }

            if (this.isFormValid()) {
                if (this.requiresSmsVerification) {
                    const customerInformationFormRef = this.$refs['customer-information-form'];
                    const customer = customerInformationFormRef.getDataToPersist();
                    this.phoneCallingCodeTmpField = customer.phone_calling_code;
                    this.phoneNumberTmpField = customer.phone_number;
                    this.countryItemTmpField = customerInformationFormRef.getCountryItem();
                    this.showSmsVerification = true;
                } else {
                    this.submitForm();
                }
            }
        },
        submitFormWithCode({code, phoneNumber, phoneCallingCode}) {
            this.submitForm(code, phoneNumber, phoneCallingCode);
        },
        submitForm(code, phoneNumber, phoneCallingCode) {
            if (this.hasLogInEnabled && !this.customer) {
                this.onRequestLogin();
                this.setLoginWithBookingCookie();
                return;
            }
            const customerInformationFormRef = this.$refs['customer-information-form'];
            const questionRefs = this.$refs['question-ref'];
            const questionsDataToPersist = [];
            if (questionRefs) {
                for (var i in questionRefs) {
                    const q = questionRefs[i];
                    questionsDataToPersist.push(q.getDataToPersist());
                }
            }

            var orderItems = this.nonEmptyCartItems.map((item) => {
                if (item.specId) {
                    return {
                        spec_id: item.specId,
                        count: item.count,
                        is_splitted: !!item.isSplitted,
                        renting_charge_amount: item.renting_charge_amount,
                        renting_charge_type: item.renting_charge_type,
                        applied_pricing_rule_id: item.appliedPricingRule?.id,
                        start: item.dateRange?.startMoment.format('YYYY-MM-DD HH:mm:ss'),
                        end: item.dateRange?.endMoment.format('YYYY-MM-DD HH:mm:ss')
                    };
                } else if (item.itemId) {
                    return {
                        item_id: item.itemId,
                        count: item.count,
                        is_splitted: !!item.isSplitted,
                        renting_charge_amount: item.renting_charge_amount,
                        renting_charge_type: item.renting_charge_type,
                        applied_pricing_rule_id: item.appliedPricingRule?.id,
                        start: item.dateRange?.startMoment.format('YYYY-MM-DD HH:mm:ss'),
                        end: item.dateRange?.endMoment.format('YYYY-MM-DD HH:mm:ss')
                    };
                } else if (item.campaign_id) {
                    return {
                        campaign_id: item.campaign_id
                    }
                } else if (item.offer_id) {
                    return {
                        offer_id: item.offer_id,
                        customer_offer_id: item.customer_offer_id ? item.customer_offer_id : null,
                        code: item.offer.promo_code ? item.offer.promo_code : null
                    }
                } else if (item.serviceId) {
                    return {
                        service_id: item.serviceId,
                        count: 1,
                        price: item.price,
                        total: item.total
                    };
                }
            });

            if (this.cartPointsDeduction) {
                orderItems.push({
                    points_deducted: this.cartPointsDeduction.points_deducted
                });
            }

            const customer = customerInformationFormRef ? customerInformationFormRef.getDataToPersist() : null;


            const data = {
                customer,
                questions: questionsDataToPersist,
                sms_code: code,
                order_items: orderItems
            }

            if (phoneNumber, phoneCallingCode) {
                data.customer.phone_number = phoneNumber;
                data.customer.phone_calling_code = phoneCallingCode;
            }

            if (this.cartLogistic) {
                data.logistic = Object.assign({
                    provider_logistic_id: this.cartLogistic.id,
                    provider_logistic_rule_id: this.cartLogisticRule ? this.cartLogisticRule.id : null
                }, this.cartAddress, {
                    receiver_name: this.receiverName,
                    receiver_phone: this.receiverPhone,
                });
            } 

            if (this.cartPaymentMethodId) {
                data.paymentMethodId = this.cartPaymentMethodId;
            }

            const confirmStringKey = (this.selectedPaymentMethod && this.selectedPaymentMethod.method === 'gateway') ? 'message.confirm_submit_booking_with_online_payment' : 'message.confirm_submit_booking_normal';

            this.promptConfirm(this.$t(confirmStringKey), this.$t('message.submit'), () => {
                let closeLoading = this.promptLoading(this.$t('message.processing'));
                this.createBooking(this.context.providerId, this.service.id, this.memberUser.id, this.date, this.startTime, (this.endDate + ' ' + this.endTime), data, (response ,error, errors) => {
                    closeLoading();
                    this.trackBook(!error);
                    if (error) {
                        var message = this.$t('message.fail_to_book');
                        var shouldAbort = true;
                        if (errors.indexOf('blocked_customer') >= 0) {
                            message = this.$t('message.fail_to_book_blocked_customer');
                        } else if (errors.indexOf('service_is_disabled') >= 0) {
                            message = this.$t('message.fail_to_book_service_is_disabled');
                        } else if (errors.indexOf('no_available_time') >= 0) {
                            message = this.$t('message.no_available_time');
                            shouldAbort = false;
                        } else if (errors.indexOf('sms_not_verified') >= 0) {
                            message = this.$t('message.wrong_sms_code');
                            shouldAbort = false;
                            this.trackVerifySms(false);
                        } else if (errors.length > 0) {
                            message = errors.join("<br>");
                            shouldAbort = false;
                        }
                        this.promptAlert(message, () => {
                            if (shouldAbort) {
                                window.location = '/';
                            }
                        });
                    } else {
                        this.$emit('booked');
                        if (code) {
                            this.trackVerifySms(true);
                        }

                        if (response.data.pending_payments && response.data.pending_payments.length > 0) {
                            const firstPayment = response.data.pending_payments[0];
                            const purchaseAmount = response.data.order ? response.data.order.total : firstPayment.amount;
                            if (data.order_items) {
                                this.trackPurchase(purchaseAmount, data.order_items);
                            }
                            if (firstPayment.order_id && firstPayment.redirectUrl) {
                                window.location = firstPayment.redirectUrl;
                                return;
                            } else if (firstPayment.order_id && firstPayment.gateway_name === 'atm') {
                                this.promptConfirm(
                                    this.$t('message.book_success_with_atm_info', {
                                        url: `https://${window.location.hostname}/?cbid=${response.data.display_id}`,
                                        bankInfo: this.provider.atm_bank_info,
                                        amount: firstPayment.amount
                                    }),
                                    this.$t('message.copy_bank_info'),
                                    () => {
                                        this.$copyText(this.$t('message.book_payment_atm_info', { bankInfo: this.provider.atm_bank_info, amount: firstPayment.amount })).then(() => {
                                            this.promptAlert(this.$t('message.atm_info_copied'), () => {
                                                window.location = '/';
                                            }, 'success');
                                        }).catch(() => {
                                            this.promptAlert(this.$t('message.sorry_copy_failed'));
                                        });
                                    },
                                    'btn btn-sm btn-primary',
                                    this.$t('general.close'),
                                    () => {
                                        window.location = '/';
                                    }
                                );
                                return;
                            }
                        }

                        this.promptAlert(this.$t('message.book_success'), () => {
                            window.location = '/';
                        }, 'success');
                    }
                });
            });
        },

        isFormValid() {
            const paymentMethodRef = this.$refs['payment-method-ref'];
            const logisticRef = this.$refs['logistic-ref'];
            const customerInformationFormRef = this.$refs['customer-information-form'];
            const questionRefs = this.$refs['question-ref'];
            var invalidElementToScrollTo = null;

            for (var i in questionRefs) {
                const q = questionRefs[i];
                if (!q.hasValidAnswer && (q.hasAnswer || q.question.is_required)) {
                    q.highlightInvalidState();
                    invalidElementToScrollTo = invalidElementToScrollTo || q;
                }
            }

            if (customerInformationFormRef && !customerInformationFormRef.hasValidAnswer) {
                if (this.hasLogInEnabled && !this.customer) {
                    this.onRequestLogin();
                    this.setLoginWithBookingCookie();
                    return;
                } else {
                    customerInformationFormRef.highlightInvalidState();
                    invalidElementToScrollTo = invalidElementToScrollTo || customerInformationFormRef;
                }
            }

            if (logisticRef && !logisticRef.hasValidInformation) {
                logisticRef.highlightInvalidState();
                invalidElementToScrollTo = invalidElementToScrollTo || logisticRef;
            }

            if (paymentMethodRef && !paymentMethodRef.hasValidInformation) {
                paymentMethodRef.highlightInvalidState();
                invalidElementToScrollTo = invalidElementToScrollTo || paymentMethodRef;
            }

            if (invalidElementToScrollTo) {
                invalidElementToScrollTo.$el.scrollIntoView(true);
                const rect = invalidElementToScrollTo.$el.getBoundingClientRect();
                const topBarEl = document.querySelector('.top-bar-container');
                if (topBarEl && rect.top < topBarEl.offsetHeight) {
                    window.scrollBy(0, -topBarEl.offsetHeight);
                }
            }

            return invalidElementToScrollTo === null;
        },
        trackBook(isSuccess) {
            const start = moment(this.context.selectedDate).format('YYYY-MM-DD HH:mm:00');
            const end = this.endDate ? moment(this.context.selectedEndDate).format('YYYY-MM-DD HH:mm:00') : null;
            window.tracker.track(() => new EventBook(this.service.id, this.memberUser.id, start, end, isSuccess));
        },
        trackPurchase(total, orderItems) {
            const start = moment(this.context.selectedDate).format('YYYY-MM-DD HH:mm:00');
            const end = this.endDate ? moment(this.context.selectedEndDate).format('YYYY-MM-DD HH:mm:00') : null;
            window.tracker.track(() => new EventPurchase(this.provider.currency, total, this.service.id, this.memberUser.id, start, end, orderItems, this.cartLogistic, this.cartAddress));
        },
        trackVerifySms(isSuccess) {
            window.tracker.track(() => new EventVerifySms('booking', isSuccess));
        },
        getQuestionSectionIndex(question) {
            return this.questions
                .filter(q => q.question.answer_type === Constants.Question.AnswerType.Section)
                .findIndex(q => q.id === question.id);
        },
        async handlePendingOrder() {
            const pendingOrderStr = localStorage.getItem('pendingOrder');
            if (!pendingOrderStr) {
                return;
            }

            const pendingOrder = JSON.parse(pendingOrderStr);
            
            // Check if order has expired
            if (pendingOrder.expiresAt < Date.now() || pendingOrder.items?.length == 0) {
                localStorage.removeItem('pendingOrder');
                return;
            }

            // Wait for items to be loaded
            const checkItemsLoaded = () => {
                return new Promise((resolve) => {
                    const check = () => {
                        console.log('Checking if all items are loaded...');
                        const allItemsLoaded = pendingOrder.items.every(item => {
                            if (item.provider_item_spec_id) {
                                const spec = this.specMap[item.provider_item_id]?.find(spec => spec.id === item.provider_item_spec_id);
                                console.log('Checking spec item:', {
                                    itemId: item.provider_item_id,
                                    specId: item.provider_item_spec_id,
                                    found: !!spec
                                });
                                return spec;
                            }
                            const item_found = !!this.itemMap[item.provider_item_id];
                            console.log('Checking regular item:', {
                                itemId: item.provider_item_id,
                                found: item_found
                            });
                            return item_found;
                        });

                        if (allItemsLoaded) {
                            console.log('All items loaded successfully');
                            resolve();
                        } else {
                            console.log('Some items not loaded yet, checking again in 100ms');
                            setTimeout(check, 100);
                        }
                    };
                    check();
                });
            };

            try {
                await checkItemsLoaded();
                
                // Add items to cart
                for (const orderItem of pendingOrder.items) {
                    if (orderItem.provider_item_spec_id) {
                        const providerItem = this.items.find(i => i.specs?.some(s => s.id === orderItem.provider_item_spec_id));
                        const spec = providerItem?.specs.find(s => s.id === orderItem.provider_item_spec_id);
                        if (spec) {
                            this.$store.dispatch('cart/setItem', {
                                item: providerItem,
                                spec,
                                count: orderItem.count
                            });
                            addedItemLines.push(providerItem.name + ' ' + spec.name + ' x ' + orderItem.count);
                        }
                    } else if (orderItem.provider_item_id) {
                        const providerItem = this.items.find(i => i.id === orderItem.provider_item_id);
                        if (providerItem) {
                            this.$store.dispatch('cart/setItem', {
                                item: providerItem,
                                count: orderItem.count
                            });
                            addedItemLines.push(providerItem.name + ' x ' + orderItem.count);
                        }
                    } else if (orderItem.booking_pass_id) {
                        // Handle booking pass items separately
                        const bookingPass = this.bookingPasses.find(bp => bp.id === orderItem.booking_pass_id);
                        if (bookingPass) {
                            this.$store.dispatch('cart/addBookingPass', {
                                bookingPass,
                                count: orderItem.count
                            });
                            addedItemLines.push(bookingPass.title + ' x ' + orderItem.count);
                        }
                    }
                }

                // Clear pending order
                localStorage.removeItem('pendingOrder');
                if (addedItemLines.length > 0) {
                    this.promptAlert(this.$t('message.items_automatically_added_to_cart', { items: addedItemLines.join('<br>') }));
                }
            } catch (error) {
                console.error('Failed to handle pending order:', error);
            }
        }
    }
}
</script>

<style lang="scss" scoped>
    /deep/ .intl-phone-input {
        width: 100%;
    }

    /deep/ .intl-phone-input input[type="text"] {
        border: solid 1px rgba(197,214,222,.7);
        box-shadow: none;
    }

    /deep/ .question-description p {
        margin: 0;
    }

    .question-container {
        border-bottom: 1px dashed #ddd;
    }

    .question-container:last-child {
        border-bottom: none;
    }
    .brief-highlight {
        font-weight: bold;
    }

    .question-form-section {
        border-radius: 0 !important;

        @media screen and (min-width: 768px) {
            border-radius: 0.25rem !important;
        }
    }

    .top-bar-container {
        background: white;
        box-shadow: 0px 0px 4px 0px #444;
        z-index: 100;
        position: fixed;
        top: 0;
        right: 0;
        left: 0;
    }

    .top-bar-right-items {
        position: absolute;
        right: 0;
        top: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        margin: 0 32px;
    }

    .logo {
        display: block;
        margin: 0 auto;
    }

    .fb-avatar-icon {
        border-radius: 20px;
    }

    .action-menu-item-icon.icon-customer-setting {
        background-image: url('/img/icon-customer-setting.png');
    }

    .action-menu-item-icon.icon-customer-logout {
        background-image: url('/img/icon-customer-logout.png');
    }

    .logo img {
        display: block;
        max-height: 60px;
        margin: 0 auto;
    }

    .login-button {
        padding: 4px 10px;
        border-radius: 2.5px;
        color: #333333;
        border: 1px solid #333333;
        font-size: 15px;
        cursor: pointer;
        user-select: none;
    }

    .login-button:hover {
        background: #333333;
        color: white;
    }
</style>

<i18n>
{
    "en": {
        "this_is_preview": "Booking Form Preview",
        "please_select_at_least_1_item": "Please select at least 1 item"
    },
    "zh": {
        "this_is_preview": "預覽 預約問卷",
        "please_select_at_least_1_item": "請至少選購一項商品哦"
    }
}
</i18n>